import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
import { getBlogs, isMicroCMSConfigured } from '../lib/microcms';
import { Blog } from '../types/blog';

export const BlogListPage = () => {
  const { categoryId } = useParams();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filteredBlogs = categoryId
    ? blogs.filter((blog) => blog.category?.id === categoryId)
    : blogs;

  const categories = blogs
    .map((blog) => blog.category)
    .filter((category): category is NonNullable<Blog['category']> => Boolean(category?.id))
    .filter((category, index, arr) => arr.findIndex((target) => target.id === category.id) === index);

  const currentCategoryName = categories.find((category) => category.id === categoryId)?.name;

  usePageMeta({
    title: currentCategoryName ? `Blog / ${currentCategoryName}` : 'Blog',
    description: currentCategoryName
      ? `WAOUHの${currentCategoryName}カテゴリの記事一覧です。`
      : 'WAOUHの制作や活動に関するブログ記事一覧です。',
    path: categoryId ? `/blog/category/${categoryId}` : '/blog',
    image: blogs[0]?.eyecatch?.url,
  });

  useEffect(() => {
    const run = async () => {
      try {
        const data = await getBlogs();
        setBlogs(data);
      } catch (e) {
        setError('記事の取得に失敗しました。microCMSの設定をご確認ください。');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  return (
    <section>
      <h1 className="page-title">{currentCategoryName ? `Blog / ${currentCategoryName}` : 'Blog'}</h1>
      {!isMicroCMSConfigured && (
        <p className="notice">
          準備中
        </p>
      )}
      {loading && <p className="notice">読み込み中です...</p>}
      {error && <p className="notice notice-error">{error}</p>}

      {!loading && categories.length > 0 && (
        <div className="category-list" aria-label="ブログカテゴリ">
          <Link to="/blog" className={`category-chip ${!categoryId ? 'is-active' : ''}`}>
            すべて
          </Link>
          {categories.map((category) => (
            <Link
              key={category.id}
              to={`/blog/category/${category.id}`}
              className={`category-chip ${category.id === categoryId ? 'is-active' : ''}`}
            >
              {category.name}
            </Link>
          ))}
        </div>
      )}

      {!loading && !error && filteredBlogs.length === 0 && (
        <p className="notice">このカテゴリの記事はまだありません。</p>
      )}

      <div className="blog-grid">
        {filteredBlogs.map((blog) => (
          <article className="blog-card" key={blog.id}>
            {blog.eyecatch?.url && (
              <Link to={`/blog/${blog.id}`} className="blog-thumb-link">
                <img src={blog.eyecatch.url} alt={blog.title} className="blog-thumb" />
              </Link>
            )}
            <div className="blog-body">
              <p className="meta">
                {dayjs(blog.publishedAt).format('YYYY.MM.DD')}
                {blog.category?.name ? ` / ${blog.category.name}` : ''}
              </p>
              <h2>
                <Link to={`/blog/${blog.id}`} className="card-title-link">
                  {blog.title}
                </Link>
              </h2>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};
